import React, { useState } from 'react';
import { ClipboardPaste, X, AlertTriangle, Check, CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { usePermissions } from '@/hooks/usePermissions';

// PasteProjectModal: paste an exported project bundle INTO the current
// project, in one of four modes (mirrored by the backend's `mode` field):
//   add     — pure copy & paste: everything added alongside existing data
//   merge   — only non-conflicting data: items whose title/name already
//             exists here are skipped (and reused as attach points)
//   upsert  — bundle wins on conflicts: matching items are overwritten in
//             place, the rest added (requires projects.task.edit)
//   replace — destructive: existing tasks/sprints/waves/threads are deleted,
//             then the bundle is inserted (requires projects.task.delete)
// Same-type only: a waterfall bundle pastes into waterfall projects, agile
// into agile. The backend enforces every rule authoritatively — the
// client-side checks just fail faster.

const BUNDLE_FORMAT = 'boss.project_bundle';

const TYPE_LABEL = {
  waterfall: 'Waterfall',
  agile: 'Agile',
  waves_threads: 'Waves & Threads',
};

const MODES = [
  {
    value: 'add',
    label: 'Add everything',
    desc: 'Adds everything alongside what exists — nothing is deleted or changed. Duplicates allowed.',
  },
  {
    value: 'merge',
    label: 'Add non-conflicting only',
    desc: "Only adds items that don't already exist here; existing items are left untouched.",
  },
  {
    value: 'upsert',
    label: 'Add & overwrite matching',
    desc: 'Upsert — matching items are overwritten with the bundle’s version, new ones are added, nothing is deleted.',
    lockedHint: 'Requires task-edit permission.',
  },
  {
    value: 'replace',
    label: 'Replace all contents',
    desc: 'Deletes all current contents, then inserts the bundle. This cannot be undone.',
    destructive: true,
    lockedHint: 'Requires task-delete permission.',
  },
];

export default function PasteProjectModal({ isOpen, onClose, project, onPasted }) {
  const { hasPermission } = usePermissions();
  const canPaste = hasPermission('projects.project.edit');
  // Replace ≈ bulk task deletion and upsert ≈ bulk task editing, so the
  // backend gates them on task.delete / task.edit on top of project.edit —
  // mirror that here by disabling the option rather than letting submit 403.
  const canDeleteTasks = hasPermission('projects.task.delete');
  const canEditTasks = hasPermission('projects.task.edit');

  const [bundle, setBundle] = useState(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [pasteMode, setPasteMode] = useState('add');
  // Replace is destructive, so committing it opens a separate confirmation
  // dialog rather than gating on an inline checkbox — a deliberate interrupt.
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setBundle(null);
    setFileName('');
    setParseError('');
    // Back to the safe default — a destructive mode should never be
    // pre-selected when the modal reopens.
    setPasteMode('add');
    setShowReplaceConfirm(false);
    setResult(null);
    setSubmitting(false);
  };

  const close = () => {
    if (!submitting) {
      reset();
      onClose?.();
    }
  };

  const finish = () => {
    reset();
    onClose?.();
    onPasted?.();
  };

  const handleFileChange = async (e) => {
    setResult(null);
    setParseError('');
    setBundle(null);
    const f = e.target.files?.[0];
    if (!f) return;
    // Re-arm the input: browsers only fire `change` when the value differs,
    // so a same-named re-pick after a parse failure would otherwise be
    // silently ignored. We already hold the File reference in `f`.
    e.target.value = '';
    setFileName(f.name);
    if (f.size > 20 * 1024 * 1024) {
      setParseError('File too large (max 20 MB).');
      return;
    }
    try {
      const text = await f.text();
      const parsed = JSON.parse(text);
      if (parsed?.format !== BUNDLE_FORMAT || !parsed?.project?.name) {
        setParseError(
          'Not a BOSS project bundle. Use "Export Project" on a project to produce one.'
        );
        return;
      }
      // Same-type guard, mirrored client-side so the user finds out before
      // submitting. The backend enforces the same rule authoritatively.
      if (parsed.project.project_type !== project?.project_type) {
        setParseError(
          `Type mismatch: this is a ${TYPE_LABEL[parsed.project.project_type] ?? parsed.project.project_type} bundle, ` +
          `but "${project?.name}" is a ${TYPE_LABEL[project?.project_type] ?? project?.project_type} project. ` +
          'Bundles can only be pasted into a project of the same type.'
        );
        return;
      }
      setBundle(parsed);
    } catch {
      setParseError('File could not be parsed as JSON.');
    }
  };

  const handleSubmit = async () => {
    if (!bundle || submitting || !project) return;
    setSubmitting(true);
    try {
      const projectId = project.project_id ?? project._id;
      const res = await api.post(
        `${API_URL}/api/projects/projects/${projectId}/paste-bundle`,
        { bundle, mode: pasteMode }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data?.detail;
        const msg = Array.isArray(detail)
          ? detail
              .map((d) => (typeof d === 'string' ? d : d?.error ?? d?.msg ?? ''))
              .filter(Boolean)
              .slice(0, 5)
              .join('; ')
          : typeof detail === 'string'
            ? detail
            : 'Paste failed.';
        toast.error(msg || 'Paste failed.');
        return;
      }
      const taskWord = `task${data.task_count === 1 ? '' : 's'}`;
      toast.success(
        pasteMode === 'replace'
          ? `Replaced contents of "${project.name}" with ${data.task_count} ${taskWord}`
          : pasteMode === 'merge'
            ? `Added ${data.task_count} ${taskWord} to "${project.name}" (${data.skipped?.tasks ?? 0} already existed)`
            : pasteMode === 'upsert'
              ? `Added ${data.task_count} ${taskWord}, overwrote ${data.updated?.tasks ?? 0} in "${project.name}"`
              : `Pasted ${data.task_count} ${taskWord} into "${project.name}"`
      );
      setResult(data);
    } catch {
      toast.error('Paste failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !canPaste) return null;

  const counts = bundle
    ? {
        tasks: bundle.tasks?.length ?? 0,
        sprints: bundle.sprints?.length ?? 0,
        waves: bundle.waves?.length ?? 0,
        threads: bundle.threads?.length ?? 0,
      }
    : null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paste-project-modal-title"
      data-testid="paste-project-modal"
    >
      <div className="absolute inset-0" onClick={close} aria-hidden="true" />

      <div className="relative bg-boss-surface border border-boss-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3 border-b border-boss-border shrink-0">
          <div>
            <h3
              id="paste-project-modal-title"
              className="text-base font-semibold text-boss-text"
            >
              Paste project bundle into &quot;{project?.name}&quot;
            </h3>
          </div>
          <button
            type="button"
            onClick={close}
            className="text-boss-text-muted hover:text-boss-text-secondary transition shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 pt-[14px] pb-[19px] space-y-7">
          {!bundle ? (
            <div className="border-2 border-dashed border-boss-border rounded-lg p-8 text-center">
              <ClipboardPaste className="mx-auto text-boss-text-muted mb-2" size={28} />
              <p className="text-sm text-boss-text-secondary mb-3">
                Pick a <span className="font-mono text-xs">.json</span> bundle of a{' '}
                <span className="font-semibold">
                  {TYPE_LABEL[project?.project_type] ?? project?.project_type}
                </span>{' '}
                project.
              </p>
              <label className="btn-secondary inline-flex items-center gap-1.5 text-xs cursor-pointer">
                <ClipboardPaste size={12} />
                Choose bundle
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="paste-project-file-input"
                />
              </label>
              {parseError ? (
                <p className="text-xs text-red-400 mt-3" data-testid="paste-project-parse-error">
                  {parseError}
                </p>
              ) : null}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between bg-boss-elevated/40 rounded-lg px-4 py-2.5">
                <span className="flex items-center gap-2 min-w-0">
                  <FileText size={16} className="shrink-0 text-[var(--boss-info)]" />
                  <span className="text-sm font-medium text-[var(--boss-info)] truncate">{fileName}</span>
                </span>
                <button
                  type="button"
                  onClick={reset}
                  disabled={submitting}
                  className="text-xs text-boss-text-muted hover:text-boss-text disabled:opacity-50"
                  data-testid="paste-project-clear"
                >
                  Clear
                </button>
              </div>

              <div className="border-t border-boss-border" aria-hidden="true" />

              <div className="space-y-3 px-4 !mt-2" data-testid="paste-project-summary">
                <div className="space-y-1.5">
                <p className="text-[11px] uppercase tracking-wider text-boss-text-secondary">
                  {pasteMode === 'replace'
                    ? 'Will replace'
                    : pasteMode === 'upsert'
                      ? 'Will update'
                      : 'Will add to'}{' '}
                  &quot;{project?.name}&quot;
                </p>
                <div className="text-base text-boss-text">
                  {counts.tasks} task{counts.tasks === 1 ? '' : 's'}
                  {counts.sprints > 0 && <>, {counts.sprints} sprint{counts.sprints === 1 ? '' : 's'}</>}
                  {counts.waves > 0 && <>, {counts.waves} wave{counts.waves === 1 ? '' : 's'}</>}
                  {counts.threads > 0 && <>, {counts.threads} thread{counts.threads === 1 ? '' : 's'}</>}
                  <span className="text-boss-text-secondary">
                    {' '}
                    — from &quot;{bundle.project?.name}&quot;
                  </span>
                </div>
                </div>
              </div>

              {!result ? (
                <div
                  className="flex flex-col gap-4 bg-boss-elevated/30 border border-boss-border/40 rounded-xl p-5 !mt-5"
                  role="radiogroup"
                  aria-label="Paste mode"
                >
                  {/* Label-on-rule header (same treatment as the danger zone
                      divider) — one element, so the gap can't go lopsided. */}
                  <div className="flex items-center gap-3 pl-4">
                    <span className="text-[10px] uppercase tracking-wider text-boss-text-secondary">
                      Paste mode
                    </span>
                    <div className="flex-1 border-t border-boss-border" aria-hidden="true" />
                  </div>
                  {MODES.map((m) => {
                    const locked =
                      (m.value === 'replace' && !canDeleteTasks) ||
                      (m.value === 'upsert' && !canEditTasks);
                    const selected = pasteMode === m.value;
                    return (
                      <React.Fragment key={m.value}>
                        {m.destructive ? (
                          // Divider quarantining the destructive option from
                          // the three additive ones.
                          <div className="flex items-center gap-2" aria-hidden="true">
                            <div className="flex-1 border-t border-boss-border" />
                            <span className="text-[10px] uppercase tracking-wider text-red-400/80">
                              Danger zone
                            </span>
                            <div className="flex-1 border-t border-boss-border" />
                          </div>
                        ) : null}
                        <button
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          aria-describedby={`paste-mode-desc-${m.value}`}
                          disabled={locked || submitting}
                          onClick={() => setPasteMode(m.value)}
                          data-testid={`paste-mode-${m.value}`}
                          className={`w-full flex items-start gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
                            m.destructive
                              ? selected
                                ? 'border-red-500/60 bg-red-500/10'
                                : 'border-red-500/30 hover:bg-red-500/5'
                              : selected
                                ? 'border-boss-border bg-boss-elevated/70'
                                : 'border-boss-border/40 hover:bg-boss-elevated/30'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {m.destructive ? (
                            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-400" />
                          ) : null}
                          <span className="min-w-0 flex-1">
                            <span
                              className={`block text-sm font-semibold ${
                                m.destructive ? 'text-red-400' : 'text-boss-text'
                              }`}
                            >
                              {m.label}
                            </span>
                            <span
                              id={`paste-mode-desc-${m.value}`}
                              className="block text-[13px] text-boss-text-secondary leading-snug mt-0.5"
                            >
                              {m.desc}
                              {locked ? ` ${m.lockedHint}` : ''}
                            </span>
                          </span>
                          {selected ? (
                            <Check
                              size={16}
                              className={`mt-0.5 shrink-0 ${
                                m.destructive ? 'text-red-400' : 'text-boss-text'
                              }`}
                            />
                          ) : null}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : null}

              {!result ? (
                <p className="text-xs text-boss-text-secondary text-center px-4 !mt-3">
                  {pasteMode === 'replace'
                    ? "This project's structure is replaced by the bundle's."
                    : 'Phases, columns, or modules the bundle needs that this project lacks are added automatically.'}
                </p>
              ) : null}

              {result ? (
                <>
                  <div className="border-t border-boss-border" aria-hidden="true" />
                  <div className="space-y-2" data-testid="paste-project-result">
                  <div className="flex items-start gap-2 rounded-md bg-green-500/10 px-3 py-2 text-sm text-green-400">
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
                    <span>
                      {result.mode === 'replace'
                        ? `Replaced contents: ${result.task_count} task${result.task_count === 1 ? '' : 's'} inserted, ${result.deleted?.tasks ?? 0} deleted`
                        : `${result.mode === 'merge' || result.mode === 'upsert' ? 'Added' : 'Pasted'} ${result.task_count} task${result.task_count === 1 ? '' : 's'}`}
                      {result.mode === 'merge' && (result.skipped?.tasks ?? 0) > 0
                        ? `; ${result.skipped.tasks} already existed (skipped)`
                        : ''}
                      {result.mode === 'upsert' && (result.updated?.tasks ?? 0) > 0
                        ? `; overwrote ${result.updated.tasks} with the bundle's version`
                        : ''}
                      {(result.added_structure?.length ?? 0) > 0
                        ? `; extended structure (${result.added_structure.length} addition${result.added_structure.length === 1 ? '' : 's'})`
                        : ''}
                      .
                    </span>
                  </div>
                  {result.paste_label ? (
                    <p className="text-[11px] text-boss-text-muted" data-testid="paste-project-label-note">
                      Pasted tasks are tagged with the label{' '}
                      <span className="font-mono text-boss-text-secondary">{result.paste_label}</span>
                    </p>
                  ) : null}
                  {(result.warnings?.length ?? 0) > 0 ? (
                    <div className="rounded-md bg-yellow-500/10 px-3 py-2">
                      <div className="flex items-center gap-2 text-sm text-yellow-400 font-medium mb-1.5">
                        <AlertTriangle size={14} />
                        {result.warnings.length} warning{result.warnings.length === 1 ? '' : 's'}
                      </div>
                      <ul className="space-y-0.5 text-xs text-yellow-200/90 max-h-[160px] overflow-y-auto pr-1">
                        {result.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  </div>
                </>
              ) : null}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-5 pt-3 border-t border-boss-border shrink-0">
          {!result ? (
            <>
              <button
                type="button"
                onClick={close}
                disabled={submitting}
                className="btn-secondary text-xs disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  pasteMode === 'replace'
                    ? setShowReplaceConfirm(true)
                    : handleSubmit()
                }
                disabled={!bundle || submitting}
                className="btn-primary inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                data-testid="paste-project-submit"
              >
                {submitting
                  ? pasteMode === 'replace'
                    ? 'Replacing…'
                    : 'Pasting…'
                  : pasteMode === 'replace'
                    ? 'Replace contents'
                    : pasteMode === 'merge'
                      ? 'Merge into project'
                      : pasteMode === 'upsert'
                        ? 'Add & overwrite'
                        : 'Paste into project'}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={finish}
              className="btn-primary text-xs"
              data-testid="paste-project-done"
            >
              Done
            </button>
          )}
        </div>
      </div>

      {/* Destructive-action interrupt: replace commits only through this
          dialog. Rendered as an absolute layer over the main panel (its own
          backdrop dims the modal behind it). */}
      {showReplaceConfirm ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center p-4 bg-black/70">
          <div
            className="absolute inset-0"
            onClick={() => setShowReplaceConfirm(false)}
            aria-hidden="true"
          />
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="paste-replace-confirm-title"
            className="relative bg-boss-surface border border-red-500/40 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4"
            data-testid="paste-replace-confirm-dialog"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="shrink-0 mt-0.5 text-red-400" />
              <div>
                <h3
                  id="paste-replace-confirm-title"
                  className="text-base font-semibold text-boss-text"
                >
                  Replace all contents?
                </h3>
                <p className="text-sm text-boss-text-secondary mt-1.5">
                  This permanently deletes every task, sprint, wave and thread
                  in &quot;{project?.name}&quot; before inserting the bundle.
                  The current contents will not be saved and this cannot be
                  undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowReplaceConfirm(false)}
                disabled={submitting}
                className="btn-secondary text-xs disabled:opacity-50"
                data-testid="paste-replace-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowReplaceConfirm(false);
                  void handleSubmit();
                }}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-md bg-red-500/90 hover:bg-red-500 text-white text-xs font-medium px-3 py-2 disabled:opacity-50"
                data-testid="paste-replace-confirm-button"
              >
                <AlertTriangle size={14} className="shrink-0" />
                Yes, replace everything
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
